/* eslint-disable max-lines */
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SpinnerLoader from "../../../../components/ui/SpinnerLoader";
import { IGTFSValidationResult } from "../../../../interfaces/endpoints/IGTFS.types";
import { getValidation } from "../../../../services/GTFS.service";

interface StatCardProps {
  label: string;
  value: string | number;
  className?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, className }) => (
  <div className="bg-gray-50 rounded-lg p-3">
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`font-medium ${className || ""}`}>{value}</p>
  </div>
);

interface MessageListProps {
  title: string;
  items: string[];
  bgClass: string;
  textClass: string;
}

const MessageList: React.FC<MessageListProps> = (
  { title, items, bgClass, textClass }
) => (
  <div>
    <p className={`text-sm font-medium ${textClass} mb-2`}>
      {title} ({items.length})
    </p>
    <ul className="space-y-1">
      {items.map((item, i) => (
        <li
          key={i}
          className={`text-sm ${bgClass} ${textClass} px-3 py-2 rounded`}
        >
          {item}
        </li>
      ))}
    </ul>
  </div>
);

const GTFSValidationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<IGTFSValidationResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getValidation(Number(id));
        setData(response.data);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    void fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <SpinnerLoader />
      </div>
    );
  }

  if (!data) {
    return (
      <p className="text-center text-gray-500 py-8">
        Validation result not found.
      </p>
    );
  }

  const errors: string[] = data.errorsJson
    ? JSON.parse(data.errorsJson) : [];
  const warnings: string[] = data.warningsJson
    ? JSON.parse(data.warningsJson) : [];

  const badgeClass = data.isValid
    ? "bg-green-100 text-green-800"
    : "bg-red-100 text-red-800";

  return (
    <>
      <Link
        to="/gtfs/feeds"
        state={["Menaxhimi i GTFS", "Feeds"]}
        className="flex items-center mb-4 text-primary"
      >
        <ChevronLeftIcon className="w-4 h-4 mr-2" />
        Kthehu tek lista e feed-ave
      </Link>
      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Validation #{data.id}
          </h2>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${badgeClass}`}>
            {data.isValid ? "Valid" : "Invalid"}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Feed Version" value={data.feedVersion} />
          <StatCard
            label="Generated At"
            value={new Date(data.generatedAt).toLocaleString()}
          />
          <StatCard label="Agencies" value={data.totalAgencies} />
          <StatCard label="Routes" value={data.totalRoutes} />
          <StatCard label="Trips" value={data.totalTrips} />
          <StatCard label="Stops" value={data.totalStops} />
          <StatCard
            label="Errors"
            value={data.errorCount}
            className="text-red-600"
          />
          <StatCard
            label="Warnings"
            value={data.warningCount}
            className="text-yellow-600"
          />
        </div>
        {data.feedUrl && (
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">
              Feed URL
            </p>
            <a
              href={data.feedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline text-sm break-all"
            >
              {data.feedUrl}
            </a>
          </div>
        )}
        {errors.length > 0 && (
          <MessageList
            title="Errors"
            items={errors}
            bgClass="bg-red-50"
            textClass="text-red-700"
          />
        )}
        {warnings.length > 0 && (
          <MessageList
            title="Warnings"
            items={warnings}
            bgClass="bg-yellow-50"
            textClass="text-yellow-700"
          />
        )}
      </div>
    </>
  );
};

export default GTFSValidationDetail;
